import { useCallback, useEffect, useRef, useState } from 'react';
import { API_GET, useConfig } from '../../api/api';
import { request } from './aiChatApi';
import { aiGroupLevel, setAiGroupLevel, useAiGrants } from './aiPermissionsCookie';

export const PERMISSION_LEVELS = ['none', 'read', 'write'];

/**
 * The feature areas the current user may delegate to the AI (from the API)
 * combined with the levels granted in this browser (cookie). Nothing is
 * granted until the user opts in; grants are sent with every prompt and
 * capped by the backend.
 */
export function useAiAgentPermissions({ active }) {
  const config = useConfig();
  const grants = useAiGrants();
  const [areas, setAreas] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const areasRef = useRef(areas);
  areasRef.current = areas;

  const load = useCallback(() => {
    if (!config) return;
    setLoading(true);
    request(config, 'ai-chats/permissions', API_GET)
      .then((res) => {
        setAreas(res.data?.groups || []);
        setError(null);
      })
      .catch(() => setError('load'))
      .finally(() => setLoading(false));
  }, [config]);

  useEffect(() => {
    if (active && !areasRef.current) load();
  }, [active, load]);

  const groups =
    areas &&
    areas.map((group) => ({ ...group, granted: aiGroupLevel(group, grants) }));

  const setLevel = useCallback(
    async (groupId, level) => {
      const group = areasRef.current?.find((g) => g.id === groupId);
      if (group) setAiGroupLevel(group, level);
    },
    [],
  );

  const anyGranted = Boolean(groups?.some((g) => g.granted !== 'none'));

  return { groups, loading, error, anyGranted, setLevel, reload: load };
}
